// @file The landside A/B arm a visitor was assigned, as properties for a conversion event here.
//
// The marketing pages at padlet.com/site/* are served by Maggi, which assigns each logged-out
// visitor an arm at the edge and stamps `experiment_key`/`experiment_variant` on every event it
// sends to PostHog. `Signed up` completes on a mozart page, so without this the only conversion
// goal an experiment can read is a CTA click on the landing page.
//
// The edge leaves two cookies per running experiment, and of that pair this module reads one.
// `maggi_x_<key>` holds the opaque visitor id the edge hashes to pick the arm: not read here, and
// nothing about it may reach an event or a person record. `maggi_xv_<key>` holds the arm name the
// edge already derived, which is what the funnel needs. Re-deriving the arm instead would mean a
// second CRC-32 and a second copy of the weight table, and the day the two disagree attribution
// is silently wrong rather than absent.
//
// Both cookies expire with their experiment, so an arm stops being reported when it stops being
// served rather than trailing whatever the browser kept.
//
// A third cookie, `maggi_xp`, marks a browser that is looking at a variant *preview* address
// rather than being in the experiment. It is session-scoped and JS-readable, and its presence
// takes over from every assignment cookie: see landsideExperimentProperties.
//
// Do not add a `renderer` property here. Maggi stamps `renderer: 'maggi'` on its own events and
// reads the key's absence as mozart, so stamping one event here would put mozart on both sides of
// that breakdown.
import { getCookie, getCookiesWithPrefix } from '@@/bits/cookie'
import { captureException } from '@@/bits/error_tracker'

const VARIANT_COOKIE_PREFIX = 'maggi_xv_'
const PREVIEW_COOKIE_NAME = 'maggi_xp'

// Keys and arm names are lowercase-hyphenated words, as content/experiments declares them.
// Anything else did not come from the edge, and a property built from it would be a breakdown
// nobody can chart.
const SLUG_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
const MAX_SLUG_LENGTH = 64

// The edge runs a handful of experiments at once, never this many. The cap bounds the work, the
// value of `experiment_count`, and how many arm properties one event can carry, against a stuffed
// cookie jar.
const MAX_ASSIGNMENTS = 8

// Named as Maggi names them, because the two sites' events have to join on one property in one
// project. `experiment_count` has no counterpart there: Maggi renders one experimented page and
// always knows which experiment its event belongs to, whereas a signup can follow visits to
// several, so the count says how many experiments this conversion belongs to.
const EXPERIMENT_KEY_PROPERTY = 'experiment_key'
const EXPERIMENT_VARIANT_PROPERTY = 'experiment_variant'
const EXPERIMENT_COUNT_PROPERTY = 'experiment_count'
const VARIANT_PREVIEW_PROPERTY = 'variant_preview'

// One property per experiment the browser is in, so a conversion can be counted in each of them
// at once. The suffix is the experiment key, which is a declared slug, so the property name is as
// enumerable as the experiment list itself.
const VARIANT_PROPERTY_PREFIX = 'experiment_variant_'

function experimentVariantProperty(key: string): string {
  return `${VARIANT_PROPERTY_PREFIX}${key}`
}

function isSlug(value: string): boolean {
  return value.length <= MAX_SLUG_LENGTH && SLUG_PATTERN.test(value)
}

/** The arms the edge has assigned this browser, by ascending key. */
function landsideExperimentAssignments(): Array<{ key: string; variant: string }> {
  return Object.entries(getCookiesWithPrefix(VARIANT_COOKIE_PREFIX))
    .filter(([key, variant]) => isSlug(key) && isSlug(variant))
    .sort(([a], [b]) => (a < b ? -1 : 1))
    .slice(0, MAX_ASSIGNMENTS)
    .map(([key, variant]) => ({ key, variant }))
}

/**
 * The arm a preview address is showing, from `maggi_xp`. The marker is `<key>:<arm>`, and a
 * browser looking at one preview at a time needs no more than that.
 *
 * A marker that is present but unreadable still counts as a preview: `previewing` is what decides
 * whether the visit is real traffic, and answering "no" to a marker the edge wrote would file a
 * preview signup in a real arm. The key and arm are reported only when they parse.
 */
function landsidePreviewMarker(): { previewing: boolean; key?: string; variant?: string } {
  const marker = getCookie(PREVIEW_COOKIE_NAME).trim()
  if (marker === '') return { previewing: false }
  const separator = marker.indexOf(':')
  const key = marker.slice(0, separator)
  const variant = marker.slice(separator + 1)
  if (separator <= 0 || !isSlug(key) || !isSlug(variant)) return { previewing: true }
  return { previewing: true, key, variant }
}

/**
 * The arm properties for a conversion event, or `{}` for the ordinary traffic that is in no
 * experiment — absent keys rather than null ones, which is what every other event here sends.
 *
 * Every assignment the browser carries is reported, as one `experiment_variant_<key>` each, so a
 * visitor who passed through two experimented pages is counted in the numerator of both. Reporting
 * only the browsers carrying exactly one assignment would instead condition on a variable
 * downstream of the arm — whether the visitor went on to visit another experimented page — which a
 * variant can change by engaging people better or by relinking a hero, and would bias the
 * measured lift against the more effective arm.
 *
 * `experiment_key`/`experiment_variant` still carry the single-assignment case, so the joins and
 * dashboards built on those two properties keep reading what they read before.
 *
 * A previewed arm replaces all of it. Someone opening a variant preview address usually already
 * holds a genuine assignment from an earlier visit, and stamping that arm would put a colleague's
 * click in a real experiment's numerator with no property left to filter it out by. Such an event
 * carries no `experiment_count`, because a preview visit is an assignment in no experiment.
 */
function landsideExperimentProperties(): Record<string, string | number | boolean> {
  try {
    return landsideExperimentPropertiesOrThrow()
  } catch (e) {
    // Reporting an arm is never worth losing a signup. The email path awaits this inside the
    // try that gates the post-signup redirect, so a throw here would leave somebody with an
    // account and no way forward. `document.cookie` is the only operation that can throw —
    // some privacy modes refuse it — and an unreported arm is a conversion missing from one
    // experiment, which is the smaller loss by far.
    captureException(e)
    return {}
  }
}

function landsideExperimentPropertiesOrThrow(): Record<string, string | number | boolean> {
  const preview = landsidePreviewMarker()
  if (preview.previewing) {
    const properties: Record<string, string | number | boolean> = { [VARIANT_PREVIEW_PROPERTY]: true }
    if (preview.key == null || preview.variant == null) return properties
    properties[EXPERIMENT_KEY_PROPERTY] = preview.key
    properties[EXPERIMENT_VARIANT_PROPERTY] = preview.variant
    properties[experimentVariantProperty(preview.key)] = preview.variant
    return properties
  }

  const assignments = landsideExperimentAssignments()
  if (assignments.length === 0) return {}
  const properties: Record<string, string | number | boolean> = {
    [EXPERIMENT_COUNT_PROPERTY]: assignments.length,
  }
  for (const { key, variant } of assignments) properties[experimentVariantProperty(key)] = variant
  if (assignments.length === 1) {
    properties[EXPERIMENT_KEY_PROPERTY] = assignments[0].key
    properties[EXPERIMENT_VARIANT_PROPERTY] = assignments[0].variant
  }
  return properties
}

export {
  EXPERIMENT_COUNT_PROPERTY,
  EXPERIMENT_KEY_PROPERTY,
  EXPERIMENT_VARIANT_PROPERTY,
  landsideExperimentProperties,
  PREVIEW_COOKIE_NAME,
  VARIANT_COOKIE_PREFIX,
  VARIANT_PREVIEW_PROPERTY,
}
